// ─────────────────────────────────────────────────────────────────────────────
// T-11 — «Distribución Generacional» con color, según la maqueta (`rep-panel-demografico` de
// `Diseño/sigma-tc-demo 31.zip` → `fundamentales-admin.js`).
//
// Adriana lo reportó así: «en la maqueta se muestran colorcitos… y aquí no». El tab pintaba una
// `RankedList` de barras planas: el dato estaba, la lectura no. La maqueta usa una tarjeta por banda
// con su punto de color, el porcentaje grande, el RANGO DE AÑOS y el conteo de personas, y resalta la
// banda mayoritaria — que es lo que permite leer «aquí manda Millennial» de un golpe en vez de
// comparando cinco cifras.
//
// 🔴 Los rangos de años NO se copiaron de la maqueta: salen de `GenerationHelper` de UserAdminService,
// que es quien CALCULA la banda de cada persona (Pew Research / Beresford). Coinciden con los de la
// maqueta, y por eso se pudo comprobar en vez de asumir — pero si algún día divergen, la fuente de
// verdad es quien calcula, no quien dibuja: una etiqueta que contradice al cálculo es peor que no
// tenerla.
//
// La maqueta dibuja CINCO bandas y el dominio tiene SEIS: le falta `Silent` (antes de 1946). Se pinta
// igual, con un neutro, en vez de esconderla — una persona real de esa banda no puede desaparecer del
// reporte porque el diseño no la previó.
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import type { RankedEntryDto } from '@/api/recognition';

/**
 * Metadatos de cada banda: la clase que trae su color (el hex vive en el CSS, nunca inline — ver
 * convención del repo) y el rango de años que se muestra bajo el nombre.
 *
 * La clave es el identificador que emite el backend (`ParticipantScore.GenerationBand`), no una
 * etiqueta: `GenZ`, no `Gen Z`. El nombre visible se deriva aparte.
 */
const BANDAS: Record<string, { clase: string; anios: string }> = {
  GenAlpha:   { clase: 'rr-gen-card--genalpha',   anios: '2013–' },
  GenZ:       { clase: 'rr-gen-card--genz',       anios: '1997–2012' },
  Millennial: { clase: 'rr-gen-card--millennial', anios: '1981–1996' },
  GenX:       { clase: 'rr-gen-card--genx',       anios: '1965–1980' },
  BabyBoomer: { clase: 'rr-gen-card--babyboomer', anios: '1946–1964' },
  Silent:     { clase: '',                        anios: '–1945' },
};

/** Orden de presentación: de la más joven a la más veterana, como en la maqueta. */
const ORDEN = ['GenAlpha', 'GenZ', 'Millennial', 'GenX', 'BabyBoomer', 'Silent'];

/**
 * Clase de color de una banda (`rr-gen-card--millennial`, …), reutilizada por
 * `BrechaGeneracionalTable` para el punto de color de cada fila — mismo color que su tarjeta aquí,
 * una sola fuente de verdad para el mapeo banda → color en vez de repetir los hex.
 */
export function claseDeBanda(id: string): string {
  return BANDAS[id]?.clase ?? '';
}

/** Etiqueta visible de una banda. `GenZ` → `Gen Z`; una banda desconocida se muestra TAL CUAL. */
export function etiquetaDeBanda(id: string): string {
  if (id === 'BabyBoomer') return 'Baby Boomer';
  if (id.startsWith('Gen') && id.length > 3) return `Gen ${id.slice(3)}`;
  return id;
}

/**
 * Orden generacional puro sobre el id de banda — extraído de `ordenarBandas` para que
 * `generationalDistributionMatrix.ts` (pivote de la hoja de Excel "Distribución Generacional") lo
 * reutilice sin duplicar el arreglo `ORDEN`: una sola fuente de verdad para "qué generación va antes".
 */
export function compararBandas(bandaA: string, bandaB: string): number {
  const indiceA = ORDEN.indexOf(bandaA);
  const indiceB = ORDEN.indexOf(bandaB);
  return (indiceA < 0 ? ORDEN.length : indiceA) - (indiceB < 0 ? ORDEN.length : indiceB);
}

/**
 * Ordena las bandas recibidas por la secuencia generacional y no por conteo.
 *
 * Exportada para poder probarla sin montar la pantalla: es la única parte con criterio. El orden
 * importa — una distribución por edades se lee como una escala, y ordenarla por tamaño rompe la
 * única lectura que la hace útil («¿hacia dónde se carga la organización?»).
 * Una banda que el dominio no conoce se pone al final en vez de descartarse.
 */
export function ordenarBandas(bandas: readonly RankedEntryDto[]): RankedEntryDto[] {
  return [...bandas].sort((a, b) => compararBandas(a.nombre, b.nombre));
}

export function DistribucionGeneracional({ bandas }: Readonly<{ bandas: readonly RankedEntryDto[] }>) {
  const { t } = useTranslation('recognition');

  const total = bandas.reduce((suma, banda) => suma + banda.count, 0);
  if (total === 0) return null;

  // La mayoritaria se calcula sobre el conteo, no sobre el orden de llegada. Con empate gana la
  // primera de la secuencia generacional, que es determinista — sin eso, dos cargas de la misma
  // pantalla podrían resaltar tarjetas distintas.
  //
  // 🔴 Las bandas SUPRIMIDAS quedan fuera del cálculo. El backend les pone el conteo en 0 (RN-V05),
  // así que sin excluirlas una pantalla donde todo está suprimido marcaría TODAS como mayoría.
  const ordenadas = ordenarBandas(bandas);
  const visibles = ordenadas.filter((banda) => !banda.suprimido);
  const maximo = visibles.length > 0 ? Math.max(...visibles.map((banda) => banda.count)) : -1;

  return (
    <div className="rr-gen-grid">
      {ordenadas.map((banda) => {
        const meta = BANDAS[banda.nombre];
        // 🔴 Una banda suprimida por el umbral de anonimato se ENMASCARA, igual que hace `RankedList`
        // con las suyas. Sin esto la tarjeta publicaría el nombre de la banda —que la lista tapa— y
        // un «0 % · 0 personas» que se lee como «medimos y no hay nadie», cuando lo que pasa es que
        // hay menos de cinco y por eso se oculta. Son dos cosas distintas y la pantalla las distingue.
        const oculta = banda.suprimido === true;
        const esMayoria = !oculta && banda.count === maximo;
        const pct = Math.round((banda.count / total) * 100);
        return (
          <div
            key={banda.id || banda.nombre}
            className={`rr-gen-card ${meta?.clase ?? ''} ${esMayoria ? 'rr-gen-card--top' : ''}`}
          >
            <div className="rr-gen-dot" />
            <div className="rr-gen-pct">{oculta ? '—' : `${pct}%`}</div>
            <div className="rr-gen-name">
              {oculta ? t('reports.common.masked') : etiquetaDeBanda(banda.nombre)}
            </div>
            {/* Una banda que el dominio no conoce no inventa rango: se queda sin la línea. El rango
                SÍ se muestra aunque esté suprimida — es una constante de calendario, no un dato de
                nadie, y es lo que permite entender qué franja quedó tapada. */}
            {meta && <div className="rr-gen-years">{meta.anios}</div>}
            <div className="rr-gen-count">
              {oculta ? '—' : t('reports.generational.people', { count: banda.count })}
              {esMayoria && ` · ${t('reports.generational.majority')}`}
            </div>
          </div>
        );
      })}
    </div>
  );
}
